<template lang="pug">
    v-dialog(
        v-model='dialog'
        max-width="600"
        persistent
        transition='dialog-bottom-transition'
    )
        v-card
            v-toolbar(v-if="isCarlieLiveHandoff" dark color="var(--carlie-purple)")
                v-avatar.mr-3(color="white" size="32")
                    img.carlie-toolbar-logo(:src="carlieLogo" alt="Carlie")
                div
                    v-toolbar-title Transfer from Carlie
                    .text-caption {{ carlieQueueLine }}
            v-toolbar(v-else dark color='warning')
                v-toolbar-title(v-if="isOutboundLead") New Lead
                v-toolbar-title(v-else) New Incoming Call

            v-card-text
                template(v-if="validPendingIncomingCall")
                    .carlie-offer.pt-4.pb-2(v-if="isCarlieLiveHandoff" :class="{ 'carlie-offer-missed-call': validCanceledMissedCall }")
                        //- Carlie's caller name lands after the offer opens, so it changes text, never rows.
                        .text-h6.text--primary.text-truncate {{ carlieCaller.name || callerPhoneNumber }}
                        .carlie-caller-line.text-body-2.text--secondary.text-truncate {{ carlieCallerLine }}
                        CarlieOfferBrief.mt-3(
                            :analysis="carlieAnalysis"
                            :handoff="carlieHandoff"
                            :is-loading="isCarlieBriefLoading"
                        )
                        .d-flex.align-center.text-body-2.error--text.mt-3(v-if="validCanceledMissedCall")
                            v-icon.mr-2(small color="error") mdi-alert
                            | Previous missed call&nbsp;
                            Timeago(:datetime="pendingIncomingCall.call_communication_queue.canceled_missed_call_created_at")

                    v-list(v-else two-line)
                        template
                            v-list-item
                                v-list-item-content
                                    v-list-item-title {{ pendingIncomingCall.call_communication_queue.organization_name }}
                                    v-list-item-subtitle.text--primary(v-text='pendingIncomingCall.call_communication_queue.campaign_name')

                        template
                            v-divider(inset)
                            v-list-item
                                v-list-item-icon
                                    v-icon(color='primary')
                                        | mdi-phone
                                v-list-item-content
                                    v-list-item-title {{ pendingIncomingCall.call_communication_queue.communication_queue_name }}
                                    v-list-item-subtitle {{ pendingIncomingCall.call_communication_queue.language_name }}

                        template
                            v-divider(inset)
                            v-list-item
                                v-list-item-icon
                                    v-icon(color='primary')
                                        | mdi-magnify
                                v-list-item-content
                                    v-list-item-title
                                        template(v-if="pendingIncomingCall.call_communication_queue.customer_match_code === 1") Perfect
                                        template(v-else-if="pendingIncomingCall.call_communication_queue.customer_match_code === 2") Multiple potential customers found
                                        template(v-else) No customer matches
                                    v-list-item-subtitle Match Status

                        template
                            v-divider(inset)
                            v-list-item
                                v-list-item-icon
                                    v-icon(color='primary')
                                        | mdi-account
                                v-list-item-content
                                    v-list-item-title
                                        template(v-if="pendingIncomingCall.call_communication_queue.customer_phone_number === '+266696687'") ANONYMOUS
                                        template(v-else) {{ pendingIncomingCall.call_communication_queue.customer_phone_number | tel }}
                                    v-list-item-subtitle Caller ID

                            v-list-item(v-if="validCustomerFullName")
                                v-list-item-icon
                                v-list-item-content
                                    v-list-item-title {{ pendingIncomingCall.call_communication_queue.customer_full_name }}
                                    v-list-item-subtitle Name

                        template(v-if="validCanceledMissedCall")
                            v-divider(inset)
                            v-list-item
                                v-list-item-icon
                                    v-icon(color='error')
                                        | mdi-alert
                                v-list-item-content
                                    v-list-item-title.error--text
                                        strong
                                            Timeago(
                                                :datetime="pendingIncomingCall.call_communication_queue.canceled_missed_call_created_at"
                                            )
                                    v-list-item-subtitle.error--text Previous Missed Call

                    v-row(align='center' justify="center")

                        v-tooltip(top)
                            template(v-slot:activator='{ on, attrs }')
                                v-btn.mx-12.mt-4(v-bind='attrs' v-on='on' fab dark color='error' @click="handleAction(false, true);")
                                    v-icon(dark)
                                        | mdi-phone-hangup
                            span Decline Call

                        v-tooltip(top)
                            template(v-slot:activator='{ on, attrs }')
                                v-btn.mx-12.mt-4(v-bind='attrs' v-on='on' fab dark color='green' @click="handleAction(true);")
                                    v-icon(dark)
                                        | mdi-phone
                            span Accept Call

</template>

<script lang="ts">
    import Vue from 'vue';

    // Assets
    import carlieLogo from '@/components/carlie/carlie_logo.png';

    // Components
    import CarlieOfferBrief from '@/components/carlie/carlie_offer_brief.vue';

    // Data
    import { GetAiCommunicationCarlieDetails } from '@/data';

    // Helpers
    import { carlieCallerName } from '@/helpers/carlie';
    import { formatTel, secondsToFriendlyElapsedTimeFormat } from '@/helpers/general';

    // The offer only lasts seconds, so Carlie's handoff details are dropped rather than waited on.
    const CARLIE_DETAILS_TIMEOUT_MS = 1500;

    export default Vue.extend({
        data() {
            return {
                carlieDetails: null,
                carlieDetailsAbortController: null,
                carlieLogo,
                dialog: true,
                isLoadingCarlieDetails: false,
                openedAt: Date.now(),
                ringingAudio: null,
                ringingAudioUrl: "https://volie-assets.s3.us-east-1.amazonaws.com/system/audio/phone_ringing_soft.mp3",
                systemAutoDeclineDuation: 13, // default
                timerDuration: 0,
                timerInterval: null,
            }
        },
        methods: {
            getCarlieDetails() {
                if (!this.isCarlieLiveHandoff) {
                    return;
                }

                this.carlieDetailsAbortController = new AbortController();
                const timeout = setTimeout(() => this.carlieDetailsAbortController?.abort(), CARLIE_DETAILS_TIMEOUT_MS);
                this.isLoadingCarlieDetails = true;

                GetAiCommunicationCarlieDetails(this.pendingIncomingCall.carlie.ai_communication_id, {}, this.carlieDetailsAbortController.signal).then((response) => {
                    this.carlieDetails = response?.Error ? null : response?.Data || null;
                }).catch(() => {
                    this.carlieDetails = null;
                }).finally(() => {
                    clearTimeout(timeout);
                    this.isLoadingCarlieDetails = false;
                });
            },
            handleAction(accept, userClicked) {

                // Stop ringing
                this.stopRinging();

                // Stop timer
                this.stopTimer();

                // Close modal
                this.dialog = false;

                // Accept or decline depending on action
                if (accept === true) {
                    this.acceptIncomingCall(this.carlieDetails);
                } else {
                    this.declineIncomingCall(userClicked === true);
                }
            },
            incrementTimer() {
                this.timerDuration += 1;
            },
            initRingingAudio() {
                if (!this.ringingAudioUrl) {
                    return;
                }

                this.ringingAudio = new Audio(this.ringingAudioUrl);
            },
            playRinging() {
                if (!this.ringingAudio) {
                    return;
                }

                this.ringingAudio.play().catch(() => {});
            },
            startTimer() {
                this.timerInterval = setInterval(this.incrementTimer, 1000);
            },
            stopRinging() {
                if (!this.ringingAudio) {
                    return;
                }

                this.ringingAudio.pause();
            },
            stopTimer() {
                if (!this.timerInterval) {
                    return;
                }

                clearInterval(this.timerInterval);
            },
        },
        computed: {
            callerPhoneNumber() {
                const phoneNumber = this.pendingIncomingCall?.call_communication_queue?.customer_phone_number;
                return phoneNumber === '+266696687' ? "ANONYMOUS" : formatTel(phoneNumber);
            },
            // The offer carries the analysis as it stood when the call was queued; the details fetch
            // is newer, so it wins whether the analysis has finished or only its fast items are in.
            carlieAnalysis() {
                if (this.carlieDetails?.analysis?.status) {
                    return this.carlieDetails.analysis;
                }
                return this.pendingIncomingCall?.carlie?.analysis || null;
            },
            carlieCaller() {
                const customerName = this.validCustomerFullName ? this.pendingIncomingCall.call_communication_queue.customer_full_name : "";
                return carlieCallerName(this.carlieHandoff?.caller_name, customerName);
            },
            carlieCallerLine() {
                const matchCode = this.pendingIncomingCall?.call_communication_queue?.customer_match_code;
                const waitingSeconds = Math.floor((this.openedAt - new Date(this.pendingIncomingCall?.call_communication_queue?.start_time).getTime()) / 1000);
                return [
                    this.carlieCaller.account ? `On ${this.carlieCaller.account}'s account` : "",
                    this.carlieCaller.name ? this.callerPhoneNumber : "",
                    matchCode === 2 ? "Multiple customer matches" : matchCode !== 1 ? "No customer match" : "",
                    waitingSeconds > 0 ? `Waiting ${secondsToFriendlyElapsedTimeFormat(waitingSeconds)}` : "",
                ].filter((detail) => !!detail).join(" · ");
            },
            carlieHandoff() {
                return this.carlieDetails?.handoff || null;
            },
            carlieQueueLine() {
                const ccq = this.pendingIncomingCall?.call_communication_queue;
                return [ccq?.organization_name, ccq?.communication_queue_name, ccq?.language_name].filter((part) => !!part).join(" · ");
            },
            // The fallback headline and the scheduling chips come from the handoff details, so the
            // brief waits for them rather than redrawing, and moving the buttons, when they land.
            isCarlieBriefLoading() {
                return this.isLoadingCarlieDetails;
            },
            isCarlieLiveHandoff() {
                return this.pendingIncomingCall?.carlie?.ai_communication_id > 0;
            },
            isOutboundLead() {
                return this.pendingIncomingCall?.call?.is_outbound_lead === true;
            },
            validCustomerFullName() {
                return this.pendingIncomingCall?.call_communication_queue?.customer_id > 0 && this.pendingIncomingCall.call_communication_queue?.customer_full_name?.length > 0;
            },
            validCanceledMissedCall() {
                return this.pendingIncomingCall?.call_communication_queue?.canceled_missed_call_id > 0;
            },
            validPendingIncomingCall() {
                return this.pendingIncomingCall?.call_communication_queue?.call_communication_queue_id > 0 && this.pendingIncomingCall?.call_communication_queue?.status === "pending";
            },
        },
        components: {
            CarlieOfferBrief,
        },
        props: {
            acceptIncomingCall: Function,
            declineIncomingCall: Function,
            pendingIncomingCall: Object,
        },
        watch: {
            timerDuration() {
                return this.dialog === true && this.timerDuration === this.systemAutoDeclineDuation ? this.handleAction(false, false) : null;
            },
        },
        created() {
            this.initRingingAudio();
            this.playRinging();
            this.startTimer();
            this.getCarlieDetails();
        },
        destroyed() {
            this.carlieDetailsAbortController?.abort();
            this.stopRinging();
            this.stopTimer(); // to be safe in avoiding memory leaks
        },
    });
</script>

<style lang="scss" scoped>
    // Matches the regular offer's height, which fits two rows of chips and a typical summary, so
    // the buttons stay put when Carlie's details land or come back short.
    .carlie-offer {
        min-height: 363px;
    }

    .carlie-offer-missed-call {
        min-height: 395px;
    }

    .carlie-caller-line {
        min-height: 1.25rem;
    }

    .carlie-toolbar-logo {
        height: 22px;
        width: 22px;
    }
</style>
